import { type JSX, useEffect, useState } from "react";

import type { Environment } from "../api/bench";
import { BenchApiError } from "../api/http";
import {
  type ProvisionResult,
  type RepositoryFile,
  type Sample,
  fetchSamples,
  provisionRepository,
} from "../api/repositories";
import { AttachedRepositories } from "./AttachedRepositories";
import { JobProgress } from "./JobProgress";

interface AttachRepositoryPanelProps {
  environment: Environment;
}

type Source = { kind: "sample"; id: string } | { kind: "files"; files: RepositoryFile[] };

/**
 * Create a repository and attach it, in one step.
 *
 * Every code operation and the SCM webhook need an attached repository, and getting one by hand
 * means creating a repo in the provider, pushing something into it, finding a credentials
 * reference and working out which hostname the Memory Service can reach it by. This does all of
 * it — either from a built-in sample shaped to exercise code intelligence, or from files you
 * supply.
 */
export function AttachRepositoryPanel({
  environment,
}: AttachRepositoryPanelProps): JSX.Element {
  // Bumped after a successful provision, so the list below picks the new repository up.
  const [listVersion, setListVersion] = useState(0);
  const [samples, setSamples] = useState<Sample[]>([]);
  const [sampleId, setSampleId] = useState("");
  const [uploaded, setUploaded] = useState<RepositoryFile[]>([]);
  const [skipped, setSkipped] = useState<string[]>([]);
  const [name, setName] = useState(suggestName());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<ProvisionResult | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    void fetchSamples(controller.signal)
      .then((found) => {
        setSamples(found);
        setSampleId(found[0]?.id ?? "");
      })
      .catch(() => {
        /* The samples list is a convenience; uploading still works without it. */
      });
    return () => controller.abort();
  }, []);

  if (!environment.can_provision_repositories) {
    return (
      <>
        <section className="panel">
          <h2>Attach a repository</h2>
          <div className="notice notice--warning">
            <strong>{environment.label} cannot create repositories.</strong> It needs{" "}
            <code>gitea_api_url</code>, <code>gitea_clone_base</code> and{" "}
            <code>gitea_token</code> configured. Without them you can still attach an existing
            repository from <em>Sources &rarr; Attach a repository</em>, supplying the clone URL
            yourself.
          </div>
        </section>
        <AttachedRepositories refreshToken={listVersion} />
      </>
    );
  }

  const chosen = samples.find((sample) => sample.id === sampleId);
  const source: Source =
    uploaded.length > 0 ? { kind: "files", files: uploaded } : { kind: "sample", id: sampleId };
  const ready = name.trim().length > 0 && (source.kind === "files" || Boolean(sampleId));

  const run = (): void => {
    setBusy(true);
    setError(null);
    setResult(null);
    void provisionRepository({
      name: name.trim(),
      ...(source.kind === "sample" ? { sample: source.id } : { files: source.files }),
      attach: true,
    })
      .then((provisioned) => {
        setResult(provisioned);
        setListVersion((version) => version + 1);
        // A repository name cannot be reused, so offer a fresh one straight away.
        setName(suggestName());
      })
      .catch((cause: unknown) => {
        setError(
          cause instanceof BenchApiError
            ? cause.message
            : `Could not create the repository: ${String(cause)}`,
        );
      })
      .finally(() => setBusy(false));
  };

  return (
    <>
      <section className="panel">
      <header className="panel__header">
        <div>
          <h2>Attach a repository</h2>
          <p className="muted">
            Creates it under <code>{environment.gitea_owner}</code>, writes the files, then hands
            it to the Memory Service to clone and index.
          </p>
        </div>
      </header>

      <div className="form-grid form-grid--tight">
        <label>
          <span>
            Repository name <em>required</em>
          </span>
          <input value={name} onChange={(event) => setName(event.target.value)} />
          <small className="muted">
            Must not already exist. Letters, digits, dot, dash or underscore.
          </small>
        </label>
      </div>

      <fieldset className="source">
        <legend>Contents</legend>

        <label className="examples__field">
          <span>Sample codebase</span>
          <select
            value={sampleId}
            disabled={uploaded.length > 0}
            onChange={(event) => setSampleId(event.target.value)}
          >
            {samples.length === 0 && <option value="">No samples available</option>}
            {samples.map((sample) => (
              <option key={sample.id} value={sample.id}>
                {sample.label} — {sample.language}, {sample.file_count} files
              </option>
            ))}
          </select>
        </label>

        {chosen && uploaded.length === 0 && (
          <>
            <p className="muted">{chosen.summary}</p>
            <details className="details">
              <summary>{chosen.file_count} files</summary>
              <ul className="source__paths">
                {chosen.paths.map((path) => (
                  <li key={path}>
                    <code>{path}</code>
                  </li>
                ))}
              </ul>
            </details>
          </>
        )}

        <p className="muted source__or">or supply your own</p>

        <div className="form-grid form-grid--tight">
          <label>
            <span>Files</span>
            <input
              type="file"
              multiple
              onChange={(event) => void takeFiles(event.target.files, setUploaded, setSkipped)}
            />
          </label>
          <label>
            <span>Whole folder</span>
            <input
              type="file"
              multiple
              /* Directory upload keeps each file's relative path, so the tree survives. */
              // @ts-expect-error -- webkitdirectory is not in the DOM typings
              webkitdirectory=""
              onChange={(event) => void takeFiles(event.target.files, setUploaded, setSkipped)}
            />
          </label>
        </div>

        {uploaded.length > 0 && (
          <div className="notice notice--info">
            <strong>
              {uploaded.length} file{uploaded.length === 1 ? "" : "s"} ready.
            </strong>{" "}
            The sample is ignored while your own files are selected.{" "}
            <button type="button" onClick={() => { setUploaded([]); setSkipped([]); }}>
              Clear
            </button>
            {skipped.length > 0 && (
              <p className="muted">
                Skipped {skipped.length} that are not text, or live under a build or VCS directory:{" "}
                <code>{skipped.slice(0, 4).join(", ")}</code>
                {skipped.length > 4 && ` and ${skipped.length - 4} more`}.
              </p>
            )}
          </div>
        )}
      </fieldset>

      {error && (
        <div className="notice notice--error">
          <strong>That did not work.</strong> {error}
        </div>
      )}

      <div className="panel__actions">
        <button type="button" className="primary" disabled={busy || !ready} onClick={run}>
          {busy ? "Creating and attaching…" : "Create and attach"}
        </button>
      </div>

      {result && <ProvisionOutcome result={result} />}
      </section>

      <AttachedRepositories refreshToken={listVersion} />
    </>
  );
}

function ProvisionOutcome({ result }: { result: ProvisionResult }): JSX.Element {
  const { repository } = result;
  return (
    <div className={result.attached ? "notice notice--info" : "notice notice--warning"}>
      <strong>
        {result.attached ? "Created and attached." : "Created, but not attached."}
      </strong>
      <ul>
        <li>
          <code>{repository.full_name}</code> —{" "}
          <a href={repository.web_url} target="_blank" rel="noreferrer">
            open in Gitea
          </a>
        </li>
        <li>
          {repository.files_written.length} files written to{" "}
          <code>{repository.default_branch}</code>
        </li>
        <li>
          The service clones <code>{repository.clone_url}</code>
        </li>
        {result.repository_id && (
          <li>
            Repository id <code>{result.repository_id}</code> — the code operations and the SCM
            webhook can use it now
          </li>
        )}
      </ul>

      {result.detail && <p>{result.detail}</p>}

      {result.call_id && result.job_id && (
        <JobProgress
          callId={result.call_id}
          job={{ job_id: result.job_id, poll_url: result.poll_url, pollable: true }}
        />
      )}

      {result.attached && (
        <p className="muted">
          Once indexing finishes, try <em>Find a symbol</em> — and its examples will resolve
          against this repository.
        </p>
      )}
    </div>
  );
}

/** Directories that are never worth uploading, and extensions that are not text. */
const SKIP_DIRECTORIES = [".git/", "node_modules/", "dist/", "build/", "__pycache__/", ".venv/"];
const TEXT_EXTENSIONS = new Set([
  "py", "ts", "tsx", "js", "jsx", "mjs", "cjs", "go", "cs", "java", "kt", "rb", "rs", "swift",
  "md", "txt", "json", "yaml", "yml", "toml", "ini", "cfg", "css", "html", "sql", "sh",
]);

async function takeFiles(
  list: FileList | null,
  setFiles: (files: RepositoryFile[]) => void,
  setSkipped: (paths: string[]) => void,
): Promise<void> {
  if (!list || list.length === 0) {
    return;
  }

  const kept: RepositoryFile[] = [];
  const skipped: string[] = [];

  for (const file of Array.from(list)) {
    const path = relativePath(file);
    const extension = path.split(".").pop()?.toLowerCase() ?? "";
    const inSkippedDirectory = SKIP_DIRECTORIES.some((directory) => path.includes(directory));

    if (inSkippedDirectory || !TEXT_EXTENSIONS.has(extension)) {
      skipped.push(path);
      continue;
    }
    kept.push({ path, content: await file.text() });
  }

  setFiles(kept);
  setSkipped(skipped);
}

/**
 * The path to write inside the repository.
 *
 * A folder upload reports paths beneath the chosen directory, so the directory's own name is
 * dropped — otherwise every file would land one level deeper than the tester selected.
 */
function relativePath(file: File): string {
  const withDirectory = (file as File & { webkitRelativePath?: string }).webkitRelativePath;
  if (!withDirectory) {
    return file.name;
  }
  const segments = withDirectory.split("/");
  return segments.length > 1 ? segments.slice(1).join("/") : withDirectory;
}

/** A distinct default name, since a repository name cannot be reused. */
function suggestName(): string {
  const stamp = new Date().toISOString().slice(0, 16).replace(/[-:T]/g, "");
  return `bench-${stamp}`;
}
